<template>
  <div>
    <media-library-collection
        v-if="multiple"
        route-prefix="transient-uploads"
        :before-upload="checkFileValidity"
        :validation-rules="validationRules"
        :name="name"
        :initial-value="initialValue"
        :max-items="maxItems"
        @change="onChange"
        ref="mediaLibraryUploader"
        @is-ready-to-submit-change="isReadyToSubmit = $event"
    >
    </media-library-collection>
    <media-library-attachment
        v-else
        route-prefix="transient-uploads"
        :before-upload="checkFileValidity"
        :validation-rules="validationRules"
        :name="name"
        :initial-value="initialValue"
        @change="onChange"
        ref="mediaLibraryUploader"
        @is-ready-to-submit-change="isReadyToSubmit = $event"
    />
    <p class="font-base mt-2 image-counter">Images count: {{ Object.keys(this.value).length }}</p>
    <div v-if="Object.keys(this.value).length < maxItems">
      <p class="mt-2 text-center">or</p>
      <div class="text-center">
        <button
            type="button"
            class="button md mt-2"
            :class="{loading}"
            :disabled="loading || !initialised"
            @click="chooser"
            v-if="allowFromDropbox"
            style="background-color:#7EBCE6"
        >
          <i class="icon dropbox-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 43 40">
              <path
                  d="M12.5 0L0 8.1l8.7 7 12.5-7.8L12.5 0zM0 21.9l12.5 8.2 8.7-7.3-12.5-7.7L0 21.9zm21.2.9l8.8 7.3L42.4 22l-8.6-6.9-12.6 7.7zM42.4 8.1L30 0l-8.8 7.3 12.6 7.8 8.6-7zM21.3 24.4l-8.8 7.3-3.7-2.5V32l12.5 7.5L33.8 32v-2.8L30 31.7l-8.7-7.3z"/>
            </svg>
          </i>
          <span>{{ !initialised ? 'Loading...' : loading ? 'Uploading, please wait... ' : 'Choose from Dropbox' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { MediaLibraryAttachment } from 'media-library-pro-vue2-attachment'
import { MediaLibraryCollection } from 'media-library-pro-vue2-collection'

export default {
  components: {
    MediaLibraryAttachment,
    MediaLibraryCollection
  },
  mounted () {
    if (this.allowFromDropbox) {
      const s = document.createElement('script')
      s.setAttribute('id', 'dropboxjs')
      s.setAttribute('data-app-key', process.env.MIX_DROPBOX_APP_KEY)
      s.setAttribute('src', 'https://www.dropbox.com/static/api/2/dropins.js')
      document.head.appendChild(s)
      s.onload = s.onreadstatechange = () => {
        this.initialised = true
      }
      return
    }
    this.initialised = true
  },
  props: {
    name: {
      type: String,
      required: true
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    accept: {
      type: Array,
      required: false
    },
    acceptDropbox: {
      type: Array,
      required: false,
    },
    maxFileSize: {
      type: Number,
      required: false
    },
    maxItems: {
      type: Number,
      required: false,
      default: null
    },
    allowFromDropbox: {
      type: Boolean,
      default: true
    },
    initialValue: {
      type: Object,
      required: false,
      default: () => {
        return {}
      }
    },
    value: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      initialised: false,
      loading: false,
      isReadyToSubmit: true,
    }
  },
  methods: {

    onChange (media) {
      this.$emit('input', media)
    },
    checkFileValidity(file) {
      return new Promise((resolve) => {
          if (file.size < 10240000) {
              return resolve();
          }

          this.$toasted.show(
            file.name + ": this image is too large, please select an image with a max file size of 10MB",
            {
              type: "error",
              duration: 30000, 
              action : {
                  text : '✕', 
                  onClick : (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              },
            }
          );
      });
    },
    chooser () {
      Dropbox.choose({

        // Required. Called when a user selects an item in the Chooser.
        success: (files) => {
          this.dropboxUpload(files)
        },

        // Optional. "preview" (default) is a preview link to the document for sharing,
        // "direct" is an expiring link to download the contents of the file. For more
        // information about link types, see Link types below.
        linkType: 'direct', // or "direct"

        // Optional. A value of false (default) limits selection to a single file, while
        // true enables multiple file selection.
        multiselect: this.multiple, // or true

        // Optional. This is a list of file extensions. If specified, the user will
        // only be able to select files with these extensions. You may also specify
        // file types, such as "video" or "images" in the list. For more information,
        // see File types below. By default, jpg and png are allowed.
        extensions: this.acceptDropbox ? this.acceptDropbox : [".jpg", ".jpeg", ".png"],

        // Optional. A value of false (default) limits selection to files,
        // while true allows the user to select both folders and files.
        // You cannot specify `linkType: "direct"` when using `folderselect: true`.
        folderselect: false, // or true

        // Optional. A limit on the size of each file that may be selected, in bytes.
        // If specified, the user will only be able to select files with size
        // less than or equal to this limit.
        // For the purposes of this option, folders have size zero.
        sizeLimit: 104857600, // or any positive number
      })
    },
    async dropboxUpload (files) {

      this.loading = true

      let counter = Object.keys(this.value).length;

      for (const file of files) {
        counter++;

        if (typeof file.link === 'undefined' || !file.link || counter > this.maxItems) {
          return
        }

        const request = {
          file_name: file.name,
          file_url: file.link
        }

        axios.post('/api/dropbox-upload', request)
            .then(response => {
              this.$refs.mediaLibraryUploader.mediaLibrary.state.media.push({
                attributes: {
                  uuid: response.data.uuid,
                  name: response.data.name,
                  order: 0,
                  custom_properties: {},
                  preview_url: response.data.preview_url,
                  original_url: response.data.original_url,
                  extension: response.data.extension,
                  size: response.data.size
                },
                client_preview: null,
                clientValidationErrors: [],
                upload: {
                  uploadProgress: 100,
                  hasFinishedUploading: true,
                  hadErrorWhileUploading: false,
                  errorMessage: null
                }
              })
              const media = this.$refs.mediaLibraryUploader.mediaLibrary.state.media.reduce((acc, item) => {
                acc[item.attributes.uuid] = item.attributes;
                return acc;
              }, {});
              this.$emit('input', media)
            })
            .catch(error => {
              const message = error.response.status === 422
                  ? error.response.data.message
                  : 'An error has occurred while attempting to upload from Dropbox, please try again'
              this.$toasted.show(message, {
                type: 'error'
              })
            })
            .then(() => this.loading = false)
      }
    }
  },
  computed: {
    validationRules () {
      return {
        accept: this.accept,
        maxSizeInKB: this.maxFileSize
      }
    }
  },
  watch: {
    isReadyToSubmit (value) {
      this.$emit('isReadyToSubmit', value)
    }
  }
}
</script>
